import { jsx } from '@emotion/react/jsx-runtime';
import { mergeRefs } from '@react-aria/utils';
import * as R from 'ramda';
import { createContext, useContext } from 'react';
import { mergeProps, useObjectRef } from 'react-aria';
import { createComponent } from './factories.js';

/**
 * Context providing prop overrides to its children.
 */ const SlotContext = /*#__PURE__*/ createContext({});
/**
 * Provider for {@link SlotContext}. New overrides will be merged with existing overrides.
 */ const SlotProvider = createComponent((props)=>{
    const { children, value } = props;
    const context = useContext(SlotContext);
    return /*#__PURE__*/ jsx(SlotContext.Provider, {
        value: R.mergeDeepRight(context, value),
        children: children
    });
});
/**
 * Hook to access the {@link SlotContext} value.
 */ const useSlotContext = ()=>useContext(SlotContext);
/**
 * Hook to get the props and ref from the slot context, based on the key.
 *
 * @param key - The key to look up the props and ref in the slot context.
 */ function useSlotProps(key) {
    const context = useSlotContext();
    return key in context ? context[key] : {};
}
/**
 * Hook to merge provided props and ref with those from the slot context, based on the key.
 *
 * @param key - The key to look up the props and ref in the slot context.
 * @param props - The props to merge with the slot props.
 * @param ref - The ref to merge with the slot ref.
 */ function useSlot(key, props, ref) {
    const { ref: slotRef, ...slotProps } = useSlotProps(key);
    const mergedProps = mergeProps(slotProps, props);
    const mergedRef = useObjectRef(ref && slotRef ? mergeRefs(ref, slotRef) : ref ?? slotRef);
    return [
        mergedProps,
        mergedRef
    ];
}

export { SlotContext, SlotProvider, useSlot, useSlotContext, useSlotProps };
//# sourceMappingURL=slots.js.map
